import { useState, useEffect, useRef } from 'react';
import { Button, Card, Icon } from '@nike/eds';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import Editor, { BeforeMount, OnMount } from '@monaco-editor/react';
import type { editor as MonacoEditor, IDisposable } from 'monaco-editor';

import Loader from '../../components/Loader';
import { QUERY_KEYS } from '../../constants';
import opensearchIndexesClient from '../../clientAPI/opensearchIndexes';
import propagate403Error from '../../utils/propagate403Error';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD';

function buildIndexName(
  id: string,
  language: string,
  generationId: number,
  legacy = false,
): string {
  const sep = legacy ? '..' : '.';
  return language
    ? `${id}.${language}${sep}${generationId}`
    : `${id}.${sep}${generationId}`;
}

interface ParsedRequest {
  method: HttpMethod;
  path: string;
  body: string;
  startLine: number; // 1-indexed
  endLine: number;
}

interface HistoryEntry {
  id: string;
  timestamp: number;
  method: HttpMethod;
  path: string;
  body?: string;
}

const HISTORY_LIMIT = 20;
const HISTORY_KEY = 'queryConsole_history';
const COMMAND_RE = /^\s*(GET|POST|PUT|DELETE|HEAD)\s+(\S.*?)\s*$/i;
const CONSOLE_LANG = 'opensearch-console';

// Match /_plugins/_sql (with optional /_explain and query string).
const SQL_PATH_RE = /^\/_plugins\/_sql(\/_explain)?(\?|$|\/)/;

function isSqlPath(path: string): boolean {
  return SQL_PATH_RE.test(path);
}

// Build the request body string for the proxy. SQL paths accept either raw
// SQL text (auto-wrapped as { query: "..." }) or a pre-shaped JSON object.
function buildRequestBody(req: ParsedRequest): {
  body?: string;
  error?: string;
} {
  const trimmed = req.body.trim();
  if (!trimmed) return { body: undefined };

  if (isSqlPath(req.path)) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return { body: JSON.stringify(parsed) };
      }
    } catch {
      // not JSON; fall through and wrap as a SQL query string
    }
    return { body: JSON.stringify({ query: trimmed }) };
  }

  try {
    const parsed = JSON.parse(trimmed);
    return { body: JSON.stringify(parsed) };
  } catch (e) {
    return { error: `Invalid JSON: ${(e as Error).message}` };
  }
}

function parseRequests(content: string): ParsedRequest[] {
  const lines = content.split('\n');
  const out: ParsedRequest[] = [];
  let cur: ParsedRequest | null = null;
  let bodyLines: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(COMMAND_RE);
    if (m) {
      if (cur) {
        cur.body = bodyLines.join('\n').trim();
        cur.endLine = i;
        out.push(cur);
      }
      cur = {
        method: m[1].toUpperCase() as HttpMethod,
        path: m[2],
        body: '',
        startLine: i + 1,
        endLine: i + 1,
      };
      bodyLines = [];
    } else if (cur) {
      bodyLines.push(lines[i]);
    }
  }
  if (cur) {
    cur.body = bodyLines.join('\n').trim();
    cur.endLine = lines.length;
    out.push(cur);
  }
  return out;
}

function findRequestAtLine(
  reqs: ParsedRequest[],
  line: number,
): ParsedRequest | undefined {
  return reqs.find((r) => line >= r.startLine && line <= r.endLine);
}

function formatBlock(method: HttpMethod, path: string, body?: string): string {
  return body && body.trim()
    ? `${method} ${path}\n${body}`
    : `${method} ${path}`;
}

// Some responses (e.g. _cat endpoints, SQL csv/raw) come back wrapped as
// { data: "<plain text>" }. Detect that and unwrap so we can render raw text.
function unwrapTextResponse(resp: unknown): string | null {
  if (
    resp &&
    typeof resp === 'object' &&
    !Array.isArray(resp) &&
    typeof (resp as { data?: unknown }).data === 'string'
  ) {
    return (resp as { data: string }).data;
  }
  return null;
}

// Suppress harmless Monaco hit-test errors
// (microsoft/monaco-editor#4527 / #4679, unfixed upstream)
function isMonacoHitTestError(err: unknown): boolean {
  if (!err) return false;
  const msg = String((err as { message?: string }).message ?? err);
  const stack = String((err as { stack?: string }).stack ?? '');
  return (
    msg.includes('offsetNode') ||
    msg.includes('hitResult') ||
    stack.includes('_doHitTestWithCaretPositionFromPoint') ||
    stack.includes('hitTestResult')
  );
}

const DEFAULT_DSL_SEED = (indexName: string) =>
  `GET /${indexName}/_search\n{\n  "query": { "match_all": {} },\n  "size": 10\n}`;

export default function QueryConsole() {
  const loaderData = useLoaderData() as { access_token: string };
  const revalidator = useRevalidator();

  const [selectedIndexId, setSelectedIndexId] = useState<string | null>(null);
  const [selectedGenerationId, setSelectedGenerationId] = useState<
    number | null
  >(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [dslContent, setDslContent] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [response, setResponse] = useState<any>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Editor / Monaco refs for CodeLens wiring
  const editorRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null);
  const langRegisteredRef = useRef(false);
  const codeLensDisposableRef = useRef<IDisposable | null>(null);
  // Stable callback ref so the editor command always sees fresh state
  const executeAtLineRef = useRef<(line: number) => void>(() => {
    /* replaced on every render */
  });

  const {
    status: indexesStatus,
    data: indexesResponse,
    error: indexesError,
  } = useQuery({
    queryKey: [QUERY_KEYS.OPENSEARCH_INDEX],
    queryFn: async () => {
      return opensearchIndexesClient.getAllIndexes();
    },
    staleTime: 60000,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  const indexes: OpenSearchIndex[] = [];
  if (indexesResponse) {
    indexesResponse.indexes.forEach((indexResponse) => {
      try {
        if (indexResponse.statusCode === 200) {
          indexes.push(JSON.parse(indexResponse.body) as OpenSearchIndex);
        }
      } catch (e) {
        console.error('Failed to parse index:', e);
      }
    });
  }

  const selectedIndex =
    indexes.find((idx) => idx.id === selectedIndexId) ?? null;
  const selectedGeneration =
    selectedIndex?.generations.find((g) => g.id === selectedGenerationId) ??
    null;
  const indexName =
    selectedIndex && selectedGeneration
      ? buildIndexName(
          selectedIndex.id,
          selectedLanguage,
          selectedGeneration.id,
          selectedIndex.legacy,
        )
      : '';

  // Suppress harmless Monaco hit-test errors while this page is mounted
  useEffect(() => {
    const errorHandler = (e: ErrorEvent) => {
      if (isMonacoHitTestError(e.error)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    const rejectionHandler = (e: PromiseRejectionEvent) => {
      if (isMonacoHitTestError(e.reason)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('error', errorHandler, true);
    window.addEventListener('unhandledrejection', rejectionHandler, true);
    return () => {
      window.removeEventListener('error', errorHandler, true);
      window.removeEventListener('unhandledrejection', rejectionHandler, true);
    };
  }, []);

  // Restore persisted state on mount
  useEffect(() => {
    try {
      const savedDsl = localStorage.getItem('queryConsole_dslContent');
      if (savedDsl) setDslContent(savedDsl);

      const savedHistory = localStorage.getItem(HISTORY_KEY);
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory);
        if (Array.isArray(parsed)) setHistory(parsed);
      }
    } catch (e) {
      console.error('Failed to restore state from localStorage:', e);
    }
  }, []);

  // Auto-select first index/generation/language; seed DSL only if editor is empty
  useEffect(() => {
    if (!indexes.length || selectedIndexId) return;
    const firstIdx = indexes[0];
    if (!firstIdx.generations.length) return;
    const firstGen = firstIdx.generations[0];
    const firstLang = firstIdx.languages?.[0] ?? '';
    setSelectedIndexId(firstIdx.id);
    setSelectedGenerationId(firstGen.id);
    setSelectedLanguage(firstLang);
    if (!dslContent.trim()) {
      setDslContent(
        DEFAULT_DSL_SEED(
          buildIndexName(firstIdx.id, firstLang, firstGen.id, firstIdx.legacy),
        ),
      );
    }
  }, [indexesResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      localStorage.setItem('queryConsole_dslContent', dslContent);
    } catch (e) {
      console.error('Failed to save dslContent to localStorage:', e);
    }
  }, [dslContent]);

  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch (e) {
      console.error('Failed to save history to localStorage:', e);
    }
  }, [history]);

  const executeMutation = useMutation({
    mutationFn: async ({
      generationEndpoint,
      queryPath,
      body,
      httpMethod,
    }: {
      generationEndpoint: string;
      queryPath: string;
      body?: string;
      httpMethod: HttpMethod;
    }) => {
      return opensearchIndexesClient.executeQuery(
        generationEndpoint,
        queryPath,
        body,
        httpMethod,
      );
    },
    onSuccess: (data) => {
      setResponse(data);
    },
    onError: (err: any) => {
      let parsedDetails: unknown = undefined;
      if (typeof err?.details === 'string') {
        try {
          parsedDetails = JSON.parse(err.details);
        } catch {
          parsedDetails = err.details;
        }
      } else if (err?.details !== undefined) {
        parsedDetails = err.details;
      }
      setResponse({
        error: err?.message || 'Query execution failed',
        ...(err?.status !== undefined ? { status: err.status } : {}),
        ...(parsedDetails !== undefined ? { details: parsedDetails } : {}),
      });
      if (err?.status === 401) {
        revalidator.revalidate();
      }
    },
  });

  propagate403Error(executeMutation.error as keyable);
  propagate403Error(indexesError as keyable);

  const pushHistory = (entry: Omit<HistoryEntry, 'id' | 'timestamp'>) => {
    const newEntry: HistoryEntry = {
      ...entry,
      id: String(Date.now()) + Math.random().toString(36).slice(2, 6),
      timestamp: Date.now(),
    };
    setHistory((prev) => [newEntry, ...prev].slice(0, HISTORY_LIMIT));
  };

  const executeDslRequest = (req: ParsedRequest) => {
    if (!selectedGeneration) {
      setResponse({ error: 'Please select an index generation first' });
      return;
    }
    const built = buildRequestBody(req);
    if (built.error) {
      setResponse({
        error: `${built.error} (in body of ${req.method} ${req.path})`,
      });
      return;
    }
    pushHistory({
      method: req.method,
      path: req.path,
      body: req.body,
    });
    executeMutation.mutate({
      generationEndpoint: selectedGeneration.endpoint,
      queryPath: req.path,
      body: built.body,
      httpMethod: req.method,
    });
  };

  // Keep the per-line execute callback fresh on every render so the
  // editor's CodeLens command sees the latest state.
  executeAtLineRef.current = (lineNumber: number) => {
    const reqs = parseRequests(dslContent);
    const req = findRequestAtLine(reqs, lineNumber);
    if (!req) {
      setResponse({ error: 'No request at this line' });
      return;
    }
    executeDslRequest(req);
  };

  const handleExecute = () => {
    if (!selectedGeneration) {
      setResponse({ error: 'Please select an index generation first' });
      return;
    }
    const reqs = parseRequests(dslContent);
    if (!reqs.length) {
      setResponse({
        error: 'No request found. Start a line with e.g. "GET /_search".',
      });
      return;
    }
    const cursorLine = editorRef.current?.getPosition()?.lineNumber ?? 0;
    const req = findRequestAtLine(reqs, cursorLine) ?? reqs[0];
    executeDslRequest(req);
  };

  const handleFormatJson = () => {
    const reqs = parseRequests(dslContent);
    if (!reqs.length) return;
    const formatted = reqs
      .map((r) => {
        let body = r.body;
        // Skip SQL bodies — they're plain text, not JSON.
        if (body.trim() && !isSqlPath(r.path)) {
          try {
            body = JSON.stringify(JSON.parse(body), null, 2);
          } catch {
            // leave invalid bodies as-is
          }
        }
        return formatBlock(r.method, r.path, body);
      })
      .join('\n\n');
    setDslContent(formatted);
  };

  const handleCopyResponse = async () => {
    if (!response) return;
    try {
      const rawText = unwrapTextResponse(response);
      const responseText =
        rawText !== null ? rawText : JSON.stringify(response, null, 2);
      await navigator.clipboard.writeText(responseText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const appendDslBlock = (block: string) => {
    setDslContent((prev) => {
      const sep = prev.trim() ? '\n\n' : '';
      return prev + sep + block;
    });
  };

  const restoreHistoryEntry = (id: string) => {
    if (!id) return;
    const entry = history.find((h) => h.id === id);
    if (!entry) return;
    appendDslBlock(formatBlock(entry.method, entry.path, entry.body));
  };

  const clearHistory = () => {
    if (!history.length) return;
    if (window.confirm('Clear query history?')) {
      setHistory([]);
    }
  };

  const catShortcuts = [
    {
      label: 'Search',
      block: indexName
        ? `GET /${indexName}/_search\n{\n  "query": { "match_all": {} },\n  "size": 10\n}`
        : '',
      title: indexName
        ? `GET /${indexName}/_search`
        : 'Select an index generation first',
      needsIndex: true,
    },
    {
      label: 'SQL',
      block: indexName
        ? `POST /_plugins/_sql?format=json\nSELECT * FROM ${indexName} LIMIT 10`
        : '',
      title: 'SQL query template',
      needsIndex: true,
    },
    {
      label: 'Indices',
      block: 'GET /_cat/indices?v',
      title: 'List all indices',
      needsIndex: false,
    },
    {
      label: 'Shards',
      block: 'GET /_cat/shards?v',
      title: 'List all shards',
      needsIndex: false,
    },
    {
      label: 'Health',
      block: 'GET /_cluster/health',
      title: 'Cluster health',
      needsIndex: false,
    },
    {
      label: 'Mapping',
      block: indexName ? `GET /${indexName}/_mapping` : '',
      title: indexName
        ? `GET /${indexName}/_mapping`
        : 'Select an index generation first',
      needsIndex: true,
    },
    {
      label: 'Settings',
      block: indexName ? `GET /${indexName}/_settings` : '',
      title: indexName
        ? `GET /${indexName}/_settings`
        : 'Select an index generation first',
      needsIndex: true,
    },
  ];

  const beforeEditorMount: BeforeMount = (monaco) => {
    if (langRegisteredRef.current) return;
    monaco.languages.register({ id: CONSOLE_LANG });
    monaco.languages.setMonarchTokensProvider(CONSOLE_LANG, {
      defaultToken: '',
      ignoreCase: false,
      tokenizer: {
        root: [
          [/^\s*(GET|POST|PUT|DELETE|HEAD)\b/, 'keyword'],
          [/\/[^\s"]+/, 'type.identifier'],
          [
            /\b(SELECT|FROM|WHERE|ORDER|BY|GROUP|HAVING|LIMIT|OFFSET|AND|OR|NOT|IN|IS|LIKE|AS|ASC|DESC|JOIN|LEFT|RIGHT|INNER|ON|NULL|DISTINCT|COUNT|SUM|AVG|MIN|MAX)\b/i,
            'keyword',
          ],
          [/"(?:[^"\\]|\\.)*"/, 'string'],
          [/'(?:[^'\\]|\\.)*'/, 'string'],
          [/\b(true|false|null)\b/, 'keyword'],
          [/-?\d+(?:\.\d+)?/, 'number'],
          [/[{}[\]]/, '@brackets'],
          [/[,:;]/, 'delimiter'],
          [/--.*$/, 'comment'],
        ],
      },
    });
    monaco.languages.setLanguageConfiguration(CONSOLE_LANG, {
      brackets: [
        ['{', '}'],
        ['[', ']'],
      ],
      autoClosingPairs: [
        { open: '{', close: '}' },
        { open: '[', close: ']' },
        { open: '"', close: '"' },
      ],
      surroundingPairs: [
        { open: '{', close: '}' },
        { open: '[', close: ']' },
        { open: '"', close: '"' },
      ],
    });
    langRegisteredRef.current = true;
  };

  const handleEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    const commandId = editor.addCommand(0, (_ctx, lineNumber: number) => {
      executeAtLineRef.current(lineNumber);
    });

    codeLensDisposableRef.current?.dispose();
    codeLensDisposableRef.current = monaco.languages.registerCodeLensProvider(
      CONSOLE_LANG,
      {
        provideCodeLenses: (model) => {
          const reqs = parseRequests(model.getValue());
          return {
            lenses: reqs.map((r, idx) => ({
              range: {
                startLineNumber: r.startLine,
                startColumn: 1,
                endLineNumber: r.startLine,
                endColumn: 1,
              },
              id: `run-${idx}-${r.startLine}`,
              command: {
                id: commandId ?? '',
                title: '\u25B6 Run',
                arguments: [r.startLine],
              },
            })),
            dispose: () => {
              /* per-list cleanup not needed */
            },
          };
        },
        resolveCodeLens: (_model, codeLens) => codeLens,
      },
    );
  };

  // Cleanup CodeLens provider on unmount
  useEffect(() => {
    return () => {
      codeLensDisposableRef.current?.dispose();
      codeLensDisposableRef.current = null;
    };
  }, []);

  if (indexesStatus === 'loading') {
    return (
      <div className='tw-w-11/12 tw-mx-auto tw-py-10'>
        <Loader />
      </div>
    );
  }

  return (
    <div className='tw-w-11/12 tw-mx-auto tw-py-10'>
      <div className='tw-mb-6'>
        <p className='eds-type--display-4'>Query Console</p>
        <p className='tw-text-sm tw-text-gray-600 tw-mt-1'>
          Execute ad-hoc queries against OpenSearch
        </p>
      </div>

      <div className='tw-grid tw-grid-cols-2 tw-gap-6'>
        {/* Request Panel */}
        <Card className='tw-p-6'>
          <div className='tw-mb-4 tw-flex tw-justify-between tw-items-center tw-gap-3'>
            <div className='tw-flex tw-items-center tw-gap-3 tw-flex-wrap'>
              <h3 className='eds-type--title-3'>Request</h3>
            </div>
            <Button
              size='small'
              variant='primary'
              onClick={handleExecute}
              disabled={
                !selectedGeneration ||
                executeMutation.isLoading ||
                !dslContent.trim()
              }
              beforeSlot={
                executeMutation.isLoading ? undefined : (
                  <Icon name='CaretRight' />
                )
              }
            >
              {executeMutation.isLoading ? 'Executing...' : 'Run at cursor'}
            </Button>
          </div>

          {/* History */}
          <div className='tw-mb-4 tw-flex tw-items-center tw-gap-2'>
            <label
              htmlFor='history-select'
              className='tw-text-xs tw-text-gray-600'
            >
              History
            </label>
            <select
              id='history-select'
              value=''
              disabled={!history.length}
              onChange={(e) => {
                restoreHistoryEntry(e.target.value);
                e.target.value = '';
              }}
              className='tw-flex-1 tw-px-2 tw-py-1 tw-text-xs tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-bg-gray-100'
            >
              <option value=''>
                {history.length
                  ? `${history.length} recent — select to append`
                  : '(no history yet)'}
              </option>
              {history.map((h) => {
                const when = new Date(h.timestamp).toLocaleTimeString();
                const preview = `${h.method} ${(h.path ?? '').slice(0, 60)}`;
                return (
                  <option key={h.id} value={h.id}>
                    {when} {preview}
                  </option>
                );
              })}
            </select>
            <button
              type='button'
              onClick={clearHistory}
              disabled={!history.length}
              className='tw-text-xs tw-text-gray-500 hover:tw-text-gray-800 disabled:tw-text-gray-300'
            >
              Clear
            </button>
          </div>

          {/* Index / Generation / Language Selection */}
          <div className='tw-mb-4 tw-grid tw-grid-cols-12 tw-gap-2'>
            <div className='tw-col-span-5'>
              <label
                htmlFor='index-select'
                className='tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1'
              >
                Index <span className='tw-text-red-600'>*</span>
              </label>
              <select
                id='index-select'
                value={selectedIndexId ?? ''}
                onChange={(e) => {
                  const id = e.target.value || null;
                  setSelectedIndexId(id);
                  const idx = indexes.find((i) => i.id === id);
                  setSelectedGenerationId(idx?.generations[0]?.id ?? null);
                  setSelectedLanguage(idx?.languages?.[0] ?? '');
                }}
                className='tw-w-full tw-px-2 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500'
              >
                {!selectedIndexId && <option value=''>Select…</option>}
                {indexes.map((idx) => (
                  <option key={idx.id} value={idx.id}>
                    {idx.id}
                  </option>
                ))}
              </select>
            </div>

            <div className='tw-col-span-5'>
              <label
                htmlFor='generation-select'
                className='tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1'
              >
                Generation <span className='tw-text-red-600'>*</span>
              </label>
              <select
                id='generation-select'
                value={selectedGenerationId ?? ''}
                disabled={!selectedIndex}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedGenerationId(v ? Number(v) : null);
                }}
                className='tw-w-full tw-px-2 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-bg-gray-100'
              >
                {!selectedGenerationId && <option value=''>Select…</option>}
                {selectedIndex?.generations.map((gen) => (
                  <option key={gen.id} value={gen.id}>
                    Gen {gen.id}
                    {gen.active ? ' (Active)' : ''}
                    {gen.readTraffic > 0 ? ` · ${gen.readTraffic}%` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className='tw-col-span-2'>
              <label
                htmlFor='language-select'
                className='tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1'
              >
                Language
              </label>
              <select
                id='language-select'
                value={selectedLanguage}
                disabled={!selectedIndex?.languages?.length}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className='tw-w-full tw-px-2 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-bg-gray-100'
              >
                {selectedIndex?.languages?.length ? (
                  selectedIndex.languages.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))
                ) : (
                  <option value=''>(none)</option>
                )}
              </select>
            </div>
          </div>
          {selectedGeneration && (
            <p className='tw-text-xs tw-text-gray-500 tw-mb-4 tw-break-all'>
              Index name: <code>{indexName}</code> &middot; Endpoint:{' '}
              {selectedGeneration.endpoint}
            </p>
          )}

          {/* Shortcuts */}
          <div className='tw-mb-3'>
            <div className='tw-text-xs tw-text-gray-600 tw-mb-1'>
              Append shortcut
            </div>
            <div className='tw-flex tw-flex-wrap tw-gap-2'>
              {catShortcuts.map((s) => (
                <button
                  key={s.label}
                  type='button'
                  onClick={() => appendDslBlock(s.block)}
                  disabled={s.needsIndex && !indexName}
                  title={s.title}
                  className='tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 disabled:tw-text-gray-400 disabled:tw-bg-gray-50 disabled:tw-cursor-not-allowed focus:tw-z-10 focus:tw-ring-2 focus:tw-ring-blue-500'
                >
                  + {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className='tw-mb-4'>
            <div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
              <div className='tw-text-sm tw-font-medium tw-text-gray-700'>
                Console <span className='tw-text-red-600'>*</span>
              </div>
              <Button
                size='small'
                variant='ghost'
                onClick={handleFormatJson}
                disabled={!dslContent.trim()}
                beforeSlot={<Icon name='Magic' />}
              >
                Format
              </Button>
            </div>
            <div
              className='tw-border tw-border-gray-300 tw-rounded-md tw-overflow-hidden'
              style={{ height: '700px' }}
            >
              <Editor
                height='100%'
                defaultLanguage={CONSOLE_LANG}
                language={CONSOLE_LANG}
                value={dslContent}
                onChange={(newValue) => setDslContent(newValue ?? '')}
                beforeMount={beforeEditorMount}
                onMount={handleEditorMount}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                  codeLens: true,
                }}
              />
            </div>
            <p className='tw-text-xs tw-text-gray-500 tw-mt-1'>
              Each request starts with <code>METHOD /path</code> on its own
              line, optionally followed by a body. JSON for DSL, raw SQL for{' '}
              <code>/_plugins/_sql</code> (auto-wrapped as{' '}
              <code>{'{ "query": "..." }'}</code>; use <code>POST</code>). Click{' '}
              <strong>▶ Run</strong> above any request, or{' '}
              <strong>Run at cursor</strong>.
            </p>
          </div>
        </Card>

        {/* Response Panel */}
        <Card className='tw-p-6'>
          <div className='tw-mb-4 tw-flex tw-justify-between tw-items-center'>
            <h3 className='eds-type--title-3'>Response</h3>
            {response && (
              <Button
                size='small'
                variant='ghost'
                onClick={handleCopyResponse}
                beforeSlot={
                  <Icon name={copySuccess ? 'CheckmarkCircle' : 'Copy'} />
                }
              >
                {copySuccess ? 'Copied!' : 'Copy'}
              </Button>
            )}
          </div>

          {executeMutation.isLoading ? (
            <Loader />
          ) : response ? (
            (() => {
              const rawText = unwrapTextResponse(response);
              const value =
                rawText !== null ? rawText : JSON.stringify(response, null, 2);
              const isText = rawText !== null;
              return (
                <div
                  className='tw-border tw-border-gray-300 tw-rounded-md tw-overflow-hidden'
                  style={{ height: '800px' }}
                >
                  <Editor
                    height='100%'
                    defaultLanguage='json'
                    language={isText ? 'plaintext' : 'json'}
                    value={value}
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      fontSize: 13,
                      scrollBeyondLastLine: false,
                      tabSize: 2,
                      wordWrap: isText ? 'off' : 'on',
                    }}
                  />
                </div>
              );
            })()
          ) : (
            <div className='tw-text-center tw-py-20 tw-text-gray-500'>
              <Icon name='Code' size='l' />
              <p className='tw-mt-4'>Execute a query to see results</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
